import React from "react";
import { Chip } from "@mui/material";
import moment from "moment";
import MDAvatar from "../../../../components/MDAvatar";
import MDBox from "../../../../components/MDBox";
import MDTypography from "../../../../components/MDTypography";
import {
    soOrderTypeMap,
    ticketPriorityOptions,
    ticketStatusOptions,
    ticketTypeOptions,
} from "../../../../constants";
import IconButton from "@mui/material/IconButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {onErrorAlert, onSuccessAlert} from "../../../../actions";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import LocalStorage from "../../../../utils/LocalStorage";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {useNavigate} from "react-router-dom";
import {checkPermission, PERMISSION_DICT} from "../../../../utils/Users_Roles_Permission";
import Highlighter from "react-highlight-words";


const TicketListItem = ({
                            ticket,
                            isSelected,
                            onClick,
                            assigneeList = [],
                            searchText = "",       // 👈 新增
                        }) => {
    const assignee = assigneeList?.find(
        (a) => a?.assigneeId === ticket?.assigneeId
    );
    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleCopy = (text) => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text)
                .then(() => {
                    dispatch(onSuccessAlert(`"${text}" ${t('copied')}`));
                })
                .catch(() => {
                    dispatch(onErrorAlert(`${t('copy failed')}`));
                });
        }
    };


    const formatFromNow = (time) => {
        const text = moment(time).fromNow();

        const map = {
            'a few seconds ago': 'just now',
            'a minute ago': '1 min ago',
            'an hour ago': '1 hr ago',
            'a day ago': '1 day ago',
            'a month ago': '1 mon ago',
            'a year ago': '1 yr ago',
        };

        if (map[text]) return map[text];

        return text
            .replace(' minutes ago', ' mins ago')
            .replace(' hours ago', ' hrs ago')
            .replace(' months ago', ' mons ago')
            .replace(' years ago', ' yrs ago');
    };

    return (
        <MDBox
            onClick={onClick}
            sx={{
                p: 1,
                mb: 0.8,
                borderRadius: 1.5,
                cursor: "pointer",
                minWidth: 270,
                backgroundColor: isSelected ? "action.selected" : "transparent",
                border: "1px solid",
                borderColor: isSelected ? "primary.main" : "divider",
                transition: "all 0.15s",
                "&:hover": {
                    backgroundColor: "action.hover",
                },
            }}
        >
            {/* 第一行：ID + 时间 */}
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox display="flex" alignItems="center" gap={0.5}>
                    <MDTypography
                        variant="button"
                        fontWeight="medium"
                        color="info"
                        sx={{ fontSize: 12 }}
                    >
                        {ticket.ticketId}
                    </MDTypography>

                    {ticket?.ticketId && (
                        <IconButton
                            size="small"
                            sx={{ p: 0, ml: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(ticket?.ticketId);
                            }}
                        >
                            <ContentCopyIcon sx={{ width: 14, height: 14 }} />
                        </IconButton>
                    )}
                </MDBox>

                <MDBox display="flex" alignItems="center" gap={0.8}>
                    <MDTypography variant="caption" fontWeight="bold">
                        {formatFromNow(ticket.editTime)}
                    </MDTypography>
                    <MDTypography variant="caption" fontSize="10px" color="text">
                        ({t('TicketManagementPage.Edited')})
                    </MDTypography>
                </MDBox>
            </MDBox>

            {/* 第二行：Order */}
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox display="flex" alignItems="center" gap={0.5}>
                    <MDTypography variant="caption" fontWeight="bold" color="primary">
                        {t('TicketManagementPage.Order No.')}: {ticket?.fromOrderNo || '-'}
                    </MDTypography>

                    {ticket?.fromOrderNo && (
                        <>
                            <IconButton
                                size="small"
                                sx={{ p: 0, ml: 0 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopy(ticket?.fromOrderNo);
                                }}
                            >
                                <ContentCopyIcon sx={{ fontSize: '15px !important' }} />
                            </IconButton>

                            {/* Jump */}
                            {[2, 3, 4].includes(ticket?.ticketType) && (
                            <IconButton
                                size="small"
                                sx={{ p: 0, ml: 0.3 }}
                                onClick={(e) => {
                                    e.stopPropagation();

                                    const orderNo = encodeURIComponent(ticket.fromOrderNo);

                                    const ownerId = ticket.ownerId;

                                    if (ticket?.ticketType === 2) {
                                        navigate(`/app/inbounds?orderNo=${orderNo}&ownerId=${ownerId}`);
                                    }

                                    if (ticket?.ticketType === 3 && ticket?.soOrderType === 1) {
                                        navigate(`/app/outbounds/wholesale?orderNo=${orderNo}&ownerId=${ownerId}`);
                                    }

                                    if (ticket?.ticketType === 3 && ticket?.soOrderType === 2) {
                                        navigate(`/app/outbounds/e-commerce?orderNo=${orderNo}&ownerId=${ownerId}`);
                                    }

                                    if (ticket?.ticketType === 4) {
                                        navigate(`/app/returns?orderNo=${orderNo}&ownerId=${ownerId}`);
                                    }

                                }}
                            >
                                <OpenInNewIcon sx={{ fontSize: '15px !important' }} />
                            </IconButton>)}
                        </>
                    )}
                </MDBox>
            </MDBox>

            {/* 第三行：用户 */}
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                <MDBox display="flex" alignItems="center" gap={0.8}>
                    <MDAvatar bgColor="light" size="sm" shadow="xxl">
                        {ticket.ownerName?.[0] || "U"}
                    </MDAvatar>

                    <MDBox display="flex" flexDirection="column" justifyContent="center">
                        <MDTypography
                            fontWeight={ticket?.unreadCount > 0 ? "bold" : "medium"}
                            variant="button"
                        >
                            {LocalStorage.getItem('role') === 'admin'
                                ? ticket?.ownerName
                                : ticket?.addWho}
                        </MDTypography>

                        <MDBox display="flex" gap={0.5} mt={0.3}>
                            <Chip
                                size="small"
                                label={
                                    t(
                                        `TicketManagementPage.${
                                            ticketTypeOptions.find(
                                                (i) => i.value === ticket.ticketType
                                            )?.label
                                        }`
                                    ) || "-"
                                }
                                variant="outlined"
                                sx={{ height: 18, fontSize: 10, fontWeight: 900 }}
                            />

                            {soOrderTypeMap[ticket.soOrderType] && (
                                <Chip
                                    size="small"
                                    label={t(`TicketManagementPage.${soOrderTypeMap[ticket.soOrderType]}`)}
                                    sx={{ height: 18, fontSize: 10, fontWeight: 900 }}
                                />
                            )}
                        </MDBox>
                    </MDBox>
                </MDBox>

                {/* 右侧：unread badge */}
                <MDBox display="flex" alignItems="center" gap={0.5}>
                    {ticket?.isNew && (
                        <MDBox
                            sx={{
                                px: 1,
                                height: 18,
                                borderRadius: "4px",
                                background: (theme) =>
                                    `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.light} 100%)`,
                                color: "#fff",
                                fontSize: 9,
                                fontWeight: 900,
                                letterSpacing: "0.08em",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                lineHeight: 1,
                                flexShrink: 0,
                                boxShadow: (theme) => `0 2px 6px ${theme.palette.info.main}70`,
                                textTransform: "uppercase",
                            }}
                        >
                            NEW
                        </MDBox>
                    )}
                    {/*{ticket?.unreadCount > 0 && (*/}
                    {/*    <MDBox*/}
                    {/*        sx={{*/}
                    {/*            minWidth: 22,*/}
                    {/*            height: 22,*/}
                    {/*            px: 0.9,*/}
                    {/*            borderRadius: "999px",*/}

                    {/*            backgroundColor: "#C76A6A",*/}
                    {/*            color: "#fff",*/}

                    {/*            fontSize: 11,*/}
                    {/*            fontWeight: 700,*/}

                    {/*            display: "flex",*/}
                    {/*            alignItems: "center",*/}
                    {/*            justifyContent: "center",*/}

                    {/*            lineHeight: 1,*/}
                    {/*            flexShrink: 0,*/}
                    {/*        }}*/}
                    {/*    >*/}
                    {/*        {ticket.unreadCount > 99 ? "99+" : ticket.unreadCount}*/}
                    {/*    </MDBox>*/}
                    {/*)}*/}
                </MDBox>
            </MDBox>

            {/* 第四行：subject ✅ 高亮 */}
            <MDBox display="flex" alignItems="center" mt={0.5}>
                <MDTypography
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                    fontWeight="medium"
                    variant="caption"
                >
                    <Highlighter
                        highlightStyle={{
                            backgroundColor: "#FFF176",
                            color: "inherit",
                            borderRadius: 2,
                            padding: "0 1px",
                        }}
                        searchWords={[searchText]}
                        autoEscape={true}
                        textToHighlight={ticket.ticketSubject || "-"}
                    />
                </MDTypography>
            </MDBox>

            {/* 第五行：chips */}
            <MDBox mt={0.5} display="flex" justifyContent="space-between" alignItems="center">
                <MDBox display="flex" gap={0.5} flexWrap="wrap">
                    <Chip
                        size="small"
                        color={
                            ticketStatusOptions.find(
                                (i) => i.value === ticket.ticketStatus
                            )?.color || "default"
                        }
                        label={
                            t(
                                `TicketManagementPage.${
                                    ticketStatusOptions.find(
                                        (i) => i.value === ticket.ticketStatus
                                    )?.label || ""
                                }`
                            ) || "-"
                        }
                        sx={{ height: 18, fontSize: 10, fontWeight: 900 }}
                    />

                    <Chip
                        size="small"
                        color={
                            ticketPriorityOptions.find(
                                (i) => i.value === ticket.ticketPriority
                            )?.color || "default"
                        }
                        label={(() => {
                            const label = ticketPriorityOptions.find(
                                (i) => i.value === ticket.ticketPriority
                            )?.label;
                            return label ? t(`TicketManagementPage.${label}`) : "-";
                        })()}
                        sx={{ height: 18, fontSize: 10, fontWeight: 900 }}
                    />
                </MDBox>

                {checkPermission(PERMISSION_DICT.QueryOwnerInfo) && (
                    <MDBox
                        sx={{
                            px: 0.8,
                            py: 0.2,
                            border: "1px solid",
                            borderColor: assignee ? "info.main" : "grey.400",
                            borderRadius: 1.5,
                            fontSize: 10,
                            fontWeight: 600,
                            color: assignee ? "info.main" : "grey.500",
                            backgroundColor: "transparent",
                            lineHeight: 1.2,
                        }}
                    >
                        {assignee?.assigneeName || t("TicketManagementPage.unassigned")}
                    </MDBox>
                )}
            </MDBox>
        </MDBox>
    );
};

export default TicketListItem